import { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, getDocs, deleteDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { creaCartellaEvento, spostaCartellaAdAcquisito } from "../utils/googleDrive";
import { gestisciReminderCalendar } from "../utils/googleCalendar";
import { sincronizzaEvento } from "../utils/googleSheets";
import { calcolaModifiche, salvaLog } from "../utils/logger";
import { creaOpportunitaCRM, creaTaskFatturazione, aggiornaImportoOpportunita, aggiornaStatoOpportunita } from "../utils/suiteCRM";

const STATO_INIZIALE = {
  titolo: "", tipoEvento: "", tipologiaEvento: [],
  dataInizio: "", oraInizio: "", dataFine: "", oraFine: "", durata: "", durataGiorni: 1,
  giornate: [],
  clienteId: "", descrizione: "",
  cartellaDrive: "", oreAlgores: "", oreCollaboratori: "", importoVendita: "",
  tecnologie: [], piattaformaStudio: "", piattaformaDistribuzione: "", tenant: "",
  chatAttiva: false, drAttiva: false, chiFiltraDR: "", canaleDR: "",
  liberatoriaRelatori: false,
  registrazioneVideo: [],
  listaDestinatari: "",
  demAttiva: false, demInvito: "", demReminder: "", demPromemoria: "",
  surveyPostEvento: false, dataSurvey: "", demSurvey: "",
  direzioneId: "", preventivoRif: "", numFattura: "", opportunitaCRM: "",
  stato: "qualificazione", motivoAnnullamento: "", nuovaData: "",
  controlloScript: "Da fare",
  idEventiCalendar: {},
  data: "", location: "", budget: "", contattiReferenti: "",
  aree: {
    audio: { attiva: false, note: "" },
    video: { attiva: false, note: "" },
    connessione: { attiva: false, note: "" },
    regia: { attiva: false, note: "" },
    contributi: { attiva: false, note: "" },
    trasmissione: { attiva: false, note: "" },
    relatori: { attiva: false, note: "" },
    noleggioMateriale: { attiva: false, note: "" },
  },
  servizi: {
    glamotion: { attivo: false, note: "" },
    multiLanguage: { attivo: false, note: "" },
    votazioniLumi: { attivo: false, note: "" },
  },
};

function rimuoviUndefined(obj) {
  if (typeof obj !== "object" || obj === null) return obj;
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v !== undefined)
      .map(([k, v]) => [k, typeof v === "object" && !Array.isArray(v) && v !== null
        ? rimuoviUndefined(v) : v])
  );
}

export function useFormEvento(clienti) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, refreshToken, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(STATO_INIZIALE);
  const [statoVecchio, setStatoVecchio] = useState(null);
  const [eventoVecchio, setEventoVecchio] = useState(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    getDoc(doc(db, "eventi", id)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setEventoVecchio(data);
        setStatoVecchio(data.stato);
        setForm((prev) => ({
          ...prev,
          ...data,
          tipologiaEvento: data.tipologiaEvento || [],
          tecnologie: data.tecnologie || [],
          registrazioneVideo: data.registrazioneVideo || [],
          giornate: data.giornate || [],
          idEventiCalendar: data.idEventiCalendar || {},
          aree: data.aree || STATO_INIZIALE.aree,
          servizi: data.servizi || STATO_INIZIALE.servizi,
        }));
      }
      setLoading(false);
    });
  }, [id]);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleMultiple(field, value) {
    setForm((prev) => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  }

  function handleDurataGiorni(valore) {
    const giorni = parseInt(valore) || 1;
    const nuoveGiornate = Array.from({ length: giorni }, (_, i) => ({
      id: i,
      dataInizio: form.giornate[i]?.dataInizio || "",
      oraInizio: form.giornate[i]?.oraInizio || "",
      dataFine: form.giornate[i]?.dataFine || "",
      oraFine: form.giornate[i]?.oraFine || "",
    }));
    setForm((prev) => ({ ...prev, durataGiorni: giorni, giornate: nuoveGiornate }));
  }

  function handleGiornata(index, field, value) {
    setForm((prev) => {
      const nuoveGiornate = [...prev.giornate];
      nuoveGiornate[index] = { ...nuoveGiornate[index], [field]: value };
      return { ...prev, giornate: nuoveGiornate };
    });
  }

  function handleArea(areaId, field, value) {
    setForm((prev) => ({
      ...prev,
      aree: {
        ...prev.aree,
        [areaId]: { ...(prev.aree?.[areaId] || {}), [field]: value },
      },
    }));
  }

  async function creaDriveManuale() {
    let token = accessToken;
    if (!token) token = await refreshToken();
    if (!token || !form.titolo || !form.clienteId) return;

    const clienteSelezionatoNome = clienti.find((c) => c.id === form.clienteId)?.nome || "";
    const dataEvento = form.durataGiorni > 1 ? form.giornate[0]?.dataInizio : form.dataInizio;

    try {
      const cartellaDrive = await creaCartellaEvento(
        token, clienteSelezionatoNome, dataEvento, form.titolo, form.stato
      );
      if (cartellaDrive && id) {
        await updateDoc(doc(db, "eventi", id), { cartellaDrive });
        setForm((prev) => ({ ...prev, cartellaDrive }));
      }
    } catch (e) {
      console.error("Errore creazione cartella Drive manuale:", e);
    }
  }

  async function handleElimina() {
    if (!id) return;
    if (!window.confirm("Sei sicuro di voler eliminare questo evento? L'operazione è irreversibile.")) return;
    try {
      // Aggiorna stato opportunità CRM a Disdetto
      if (form.opportunitaCRM) {
        await aggiornaStatoOpportunita(form.opportunitaCRM, "annullato");
      }
      await deleteDoc(doc(db, "eventi", id));
      navigate("/");
    } catch (error) {
      console.error("Errore eliminazione evento:", error);
    }
  }

  async function handleSalva() {
    setSaving(true);
    try {
      let token = accessToken;
      if (!token) token = await refreshToken();

      const clienteSelezionatoNome = clienti.find((c) => c.id === form.clienteId)?.nome || "";
      const clientiMap = Object.fromEntries(clienti.map((c) => [c.id, c.nome]));

      let nuovoStato = form.stato;
      if (statoVecchio === "qualificazione" && id) {
        nuovoStato = "preventivo";
      }

      const formAggiornato = { ...form, stato: nuovoStato };

      const datiDaSalvare = rimuoviUndefined({
        ...formAggiornato,
        cartellaDrive: form.cartellaDrive || "",
        idEventiCalendar: rimuoviUndefined(form.idEventiCalendar || {}),
        updatedAt: serverTimestamp(),
      });

      let eventoId = id;
      if (id) {
        await updateDoc(doc(db, "eventi", id), datiDaSalvare);
      } else {
        const ref = await addDoc(collection(db, "eventi"), {
          ...datiDaSalvare,
          createdAt: serverTimestamp(),
        });
        eventoId = ref.id;
      }

      // Log operazione
      const modifiche = calcolaModifiche(eventoVecchio, formAggiornato, clientiMap);
      const tipoOperazione = !id ? "Creazione evento" :
        statoVecchio !== nuovoStato ? `Cambio stato: ${statoVecchio} → ${nuovoStato}` :
        "Modifica evento";
      salvaLog(currentUser, tipoOperazione, eventoId, formAggiornato.titolo, modifiche);

      // Tutte le sincronizzazioni esterne (Drive, Calendar, Sheets, CRM) vengono attese prima di
      // navigare via: se si naviga subito e si rientra sullo stesso evento prima che finiscano,
      // il salvataggio successivo legge da Firestore id/campi non ancora aggiornati (es.
      // idEventiCalendar) e li ricrea da zero invece di aggiornarli — vedi il bug dei promemoria
      // Calendar duplicati.
      const sincronizzazioniEsterne = [];

      if (token) {
        // Drive — crea cartella quando passa da qualificazione a preventivo
        if (form.titolo && form.clienteId && !form.cartellaDrive && nuovoStato === "preventivo") {
          const dataEvento = form.durataGiorni > 1
            ? form.giornate[0]?.dataInizio
            : form.dataInizio;
          sincronizzazioniEsterne.push(
            creaCartellaEvento(token, clienteSelezionatoNome, dataEvento, form.titolo, nuovoStato)
              .then((cartellaDrive) => {
                if (cartellaDrive) {
                  return updateDoc(doc(db, "eventi", eventoId), { cartellaDrive });
                }
              })
              .catch((e) => console.error("Errore creazione cartella Drive:", e))
          );
        }

        // Drive — sposta da Opportunity a Operations
        if (nuovoStato === "chiusoAcquisito" && statoVecchio === "preventivo" && form.cartellaDrive) {
          sincronizzazioniEsterne.push(
            spostaCartellaAdAcquisito(token, clienteSelezionatoNome, form.cartellaDrive)
              .then((nuovaCartella) => {
                if (nuovaCartella) {
                  return updateDoc(doc(db, "eventi", eventoId), { cartellaDrive: nuovaCartella });
                }
              })
              .catch((e) => console.error("Errore spostamento cartella Drive:", e))
          );
        }

        // Calendar — reminder
        if (nuovoStato === "chiusoAcquisito") {
          sincronizzazioniEsterne.push(
            getDoc(doc(db, "impostazioni", "calendario"))
              .then(async (snap) => {
                const impostazioni = snap.exists() ? snap.data() : {};
                const nuoviIdEventi = await gestisciReminderCalendar(
                  token, formAggiornato, eventoVecchio, impostazioni,
                  clienteSelezionatoNome, ""
                );
                if (nuoviIdEventi) {
                  return updateDoc(doc(db, "eventi", eventoId), {
                    idEventiCalendar: rimuoviUndefined(nuoviIdEventi)
                  });
                }
              })
              .catch((e) => console.error("Errore reminder Calendar:", e))
          );
        }

        // Google Sheets — sincronizza solo se chiusoAcquisito o successivo
        if (["chiusoAcquisito", "completato", "fatturato"].includes(nuovoStato)) {
          sincronizzazioniEsterne.push(
            Promise.all([
              getDocs(collection(db, "clienti")),
              getDocs(collection(db, "tipologieEvento")),
              getDocs(collection(db, "tecnologie")),
            ]).then(([clientiSnap, tipologieSnap, tecnologieSnap]) => {
              const clientiMapFull = {};
              clientiSnap.docs.forEach((d) => { clientiMapFull[d.id] = d.data().nome; });
              const tipologieMap = {};
              tipologieSnap.docs.forEach((d) => { tipologieMap[d.id] = d.data().nome; });
              const tecnologieMap = {};
              tecnologieSnap.docs.forEach((d) => { tecnologieMap[d.id] = d.data().nome; });

              return sincronizzaEvento(token, formAggiornato, clientiMapFull, tipologieMap, tecnologieMap)
                .catch((e) => console.error("Errore sincronizzazione Sheets:", e));
            })
          );
        }
        // SuiteCRM — aggiorna importo opportunità ad ogni salvataggio
        if (formAggiornato.opportunitaCRM && formAggiornato.importoVendita) {
          sincronizzazioniEsterne.push(
            aggiornaImportoOpportunita(formAggiornato.opportunitaCRM, formAggiornato.importoVendita)
              .catch((e) => console.error("Errore aggiornamento importo CRM:", e))
          );
        }
        // SuiteCRM — crea opportunità quando passa a preventivo, solo se è stato scelto un
        // Account CRM (senza, resterebbe orfana) e non ne è già collegata una (sarebbe un
        // duplicato). L'ID va in opportunitaCRM, il campo da cui leggono gli aggiornamenti di
        // importo e stato; opportunitaCRMId resta valorizzato per compatibilità.
        if (nuovoStato === "preventivo" && statoVecchio === "qualificazione" &&
          formAggiornato.accountCRMId && !formAggiornato.opportunitaCRM) {
          sincronizzazioniEsterne.push(
            creaOpportunitaCRM(formAggiornato, clienteSelezionatoNome, formAggiornato.accountCRMId)
              .then((crmId) => {
                if (crmId) {
                  return updateDoc(doc(db, "eventi", eventoId), { opportunitaCRM: crmId, opportunitaCRMId: crmId });
                }
              })
              .catch((e) => console.error("Errore CRM opportunità:", e))
          );
        }

        // SuiteCRM — crea task fatturazione quando completato
        if (nuovoStato === "completato" && statoVecchio !== "completato") {
          sincronizzazioniEsterne.push(
            creaTaskFatturazione(formAggiornato, clienteSelezionatoNome)
              .then((taskId) => {
                if (taskId) {
                  return updateDoc(doc(db, "eventi", eventoId), { taskFatturazioneId: taskId });
                }
              })
              .catch((e) => console.error("Errore CRM task:", e))
          );
        }
        // SuiteCRM — aggiorna stato opportunità
        if (formAggiornato.opportunitaCRM &&
          ["chiusoAcquisito", "chiusoPerso", "annullato"].includes(nuovoStato) &&
          statoVecchio !== nuovoStato) {
          sincronizzazioniEsterne.push(
            aggiornaStatoOpportunita(formAggiornato.opportunitaCRM, nuovoStato)
              .catch((e) => console.error("Errore aggiornamento stato CRM:", e))
          );
        }
      }

      await Promise.all(sincronizzazioniEsterne);
      navigate("/");

    } catch (error) {
      console.error("Errore salvataggio:", error);
      setSaving(false);
      return;
    }
    setSaving(false);
  }

  return {
    id, form, loading, saving,
    handleChange, toggleMultiple, handleDurataGiorni, handleGiornata, handleArea, handleSalva,
    creaDriveManuale, handleElimina,
  };
}